import { useMemo } from 'react';
import type { Options, AlignedData } from 'uplot';
import { ChartCanvas, readCssVar } from './charts/ChartCanvas';
import { ChartColorFallback } from '@/tokens/chart-color-fallbacks';
import type { StatusLevel } from './StatusPill';

export type ScalarPoint = { t: number; v: number };

export type ScalarTrackProps = {
  /** Metric name, e.g. "RMS". */
  metric: string;
  /** Engineering unit, e.g. "mm/s" or "—". */
  unit: string;
  /** Time series, sorted ascending by `t` (Unix seconds). */
  points: ScalarPoint[];
  /** Last reading + severity tone for the right-side chip. */
  last: { value: number; tone: StatusLevel };
  /** Fraction (0..1) of the visible window for the linked cursor. */
  cursorFraction?: number | null;
  /** Visual height tier — `compact` for the 4-stack on S5. */
  size?: 'compact' | 'standard';
  /** Optional secondary series for the "+ Compare asset" overlay.
   *  Renders as a dashed line so the operator can tell primary from
   *  compared at a glance. Pair with `compareLabel` for the legend. */
  compare?: ScalarPoint[];
  compareLabel?: string;
};

const TONE_VAR: Record<StatusLevel, string> = {
  critical: 'color-critical',
  warn: 'color-warn',
  watch: 'color-watch',
  healthy: 'color-healthy',
  info: 'color-info',
};

const TONE_BG: Record<StatusLevel, string> = {
  critical: 'bg-critical-bg text-critical-fg',
  warn: 'bg-warn-bg text-warn-fg',
  watch: 'bg-watch-bg text-watch-fg',
  healthy: 'bg-healthy-bg text-healthy-fg',
  info: 'bg-info-bg text-info-fg',
};

/**
 * Single-metric trend over time. Designed for the S5 scalar strip
 * (RMS / Peak / Crest / Kurtosis stacked on a shared x-axis) — keeps
 * the chart chrome quiet (no axes labels per track since the strip
 * shares one time axis) and surfaces the latest value as a coloured
 * chip on the right so the operator reads "is this high right now?"
 * before scanning the line shape.
 */
export function ScalarTrack({
  metric,
  unit,
  points,
  last,
  cursorFraction = null,
  size = 'compact',
  compare,
  compareLabel,
}: ScalarTrackProps) {
  // Compose AlignedData with both series sharing one x-axis. When
  // the timestamps don't align (e.g. compare comes from a different
  // sensor at a different cadence), we resample the compare series
  // onto the primary's timeline via nearest-neighbour. Both series
  // are normalised to fixed-length Float64Arrays so uPlot's
  // typed-array fast path is honoured.
  const data = useMemo<AlignedData>(() => {
    const xs = new Float64Array(points.length);
    const ys = new Float64Array(points.length);
    for (let i = 0; i < points.length; i++) {
      xs[i] = points[i].t;
      ys[i] = points[i].v;
    }
    if (!compare || compare.length === 0) {
      return [xs, ys];
    }
    const ys2 = new Float64Array(points.length);
    // O(n+m) nearest-neighbour: walk the compare series with a
    // pointer, advancing whenever it falls behind the primary x.
    let j = 0;
    for (let i = 0; i < points.length; i++) {
      while (j + 1 < compare.length && Math.abs(compare[j + 1].t - xs[i]) <= Math.abs(compare[j].t - xs[i])) {
        j++;
      }
      ys2[i] = compare[j].v;
    }
    return [xs, ys, ys2];
  }, [points, compare]);

  const valueRange = useMemo<{ min: number; max: number }>(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of points) {
      if (p.v < min) min = p.v;
      if (p.v > max) max = p.v;
    }
    // Honour the compare overlay's range too so neither line clips.
    if (compare) {
      for (const p of compare) {
        if (p.v < min) min = p.v;
        if (p.v > max) max = p.v;
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { min: 0, max: 1 };
    }
    if (min === max) {
      return { min: min - 0.1, max: max + 0.1 };
    }
    const pad = (max - min) * 0.1;
    return { min: min - pad, max: max + pad };
  }, [points, compare]);

  const options = useMemo<Omit<Options, 'width' | 'height'>>(() => {
    const stroke = readCssVar(TONE_VAR[last.tone], ChartColorFallback.info);
    const grid = readCssVar('color-gray-300', ChartColorFallback.gray300);
    const axis = readCssVar('color-gray-700', ChartColorFallback.gray700);
    return {
      scales: {
        // No static `range` — uPlot ignores user setScale calls when a
        // static range is set. See SpectrumPanel for the writeup.
        x: { time: true },
        y: {},
      },
      axes: [
        // Hidden x-axis on compact tracks — the strip shares one
        // axis on the bottom. Y-axis stays visible so the operator
        // can read the scale.
        { show: size !== 'compact', stroke: axis, grid: { stroke: grid, width: 0.5 } },
        { stroke: axis, grid: { stroke: grid, width: 0.5 }, size: 50 },
      ],
      series: [
        { label: 'time' },
        {
          label: metric,
          stroke,
          width: 1.4,
          points: { show: false },
        },
        ...(compare
          ? [
              {
                label: compareLabel ?? 'compare',
                stroke: readCssVar('color-gray-700', ChartColorFallback.gray700),
                width: 1.1,
                dash: [4, 3],
                points: { show: false },
              },
            ]
          : []),
      ],
      cursor: {
        x: true,
        y: false,
        drag: { x: true, y: false, setScale: true },
      },
      legend: { show: false },
      hooks: {
        init: [
          (u) => {
            if (points.length > 0) {
              u.setScale('x', { min: points[0].t, max: points[points.length - 1].t });
            }
            u.setScale('y', { min: valueRange.min, max: valueRange.max });
          },
        ],
        drawClear: [
          (u) => {
            if (cursorFraction == null) return;
            const f = Math.max(0, Math.min(1, cursorFraction));
            const xMin = u.scales.x.min ?? 0;
            const xMax = u.scales.x.max ?? 1;
            const x = Math.round(u.valToPos(xMin + (xMax - xMin) * f, 'x', true));
            u.ctx.save();
            u.ctx.setLineDash([4, 2]);
            u.ctx.lineWidth = 1.2;
            u.ctx.strokeStyle = readCssVar('color-warn', ChartColorFallback.warn);
            u.ctx.beginPath();
            u.ctx.moveTo(x, u.bbox.top);
            u.ctx.lineTo(x, u.bbox.top + u.bbox.height);
            u.ctx.stroke();
            u.ctx.restore();
          },
        ],
      },
    };
  }, [metric, last.tone, points, cursorFraction, valueRange, size]);

  const aspect = size === 'compact' ? 12 : 6;
  const lastDisplay = formatScalar(last.value, unit);

  return (
    <div className="grid grid-cols-[6rem_minmax(0,1fr)_auto] items-center gap-sp-3 border-b border-gray-300 px-sp-3 py-sp-2 last:border-b-0">
      <div className="min-w-0">
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          {metric}
        </p>
        <p className="font-mono text-fs-meta text-gray-700">
          {unit === '—' ? 'ratio' : unit}
        </p>
      </div>
      <div className="min-w-0">
        {points.length === 0 ? (
          <div className="text-fs-meta text-gray-700">No samples in window</div>
        ) : (
          <ChartCanvas
            data={data}
            options={options}
            aspect={aspect}
            ariaLabel={`${metric} trend, latest ${lastDisplay}`}
          />
        )}
      </div>
      <span
        className={
          'inline-flex items-center justify-end rounded-r-chip px-sp-2 py-sp-1 font-mono text-fs-meta ' +
          TONE_BG[last.tone]
        }
        aria-label={`Latest ${metric} ${lastDisplay}, ${last.tone}`}
      >
        {lastDisplay}
      </span>
    </div>
  );
}

function formatScalar(v: number, unit: string): string {
  const fixed = Math.abs(v) < 1 ? v.toFixed(3) : v.toPrecision(3);
  return unit === '—' ? fixed : `${fixed} ${unit}`;
}
